import { useContext, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import {
  CurrentLocationState,
  PartnerStore,
  PartnerStoreBrowse
} from '../types'
import { PseudoFormInput } from '../LocationSelect/FormControls'
import { IoIosSearch } from 'react-icons/io'
import BrowseStoresList from './BrowseStoresList'
import { LandingContext } from '../LandingContext'
import {
  useGoogleApis,
  useIntersectionObserver,
  useLandingLocation,
  useSessionStorage,
  useShop
} from 'shop/hooks'
import { filterStores } from './utils'
import { isAddressEligible } from '../LocationSelect/AddressSuggestions'
import {
  ControlsHeaderSentinel,
  ControlsHeaderSticky
} from '../LocationSelect/LocationSelectModal'
import StoreSortControl from './StoreSort/StoreSortControl'
import { ClosedStoresMessage } from '../commonStyles'
import { autofillLocation } from '../LocationSelect/utils'

type Props = {
  stores?: PartnerStore[]
}

const LandingStoreList = ({ stores }: Props) => {
  const {
    setCurrentLocation,
    setUseCustomerAddress,
    currentLocation,
    sortedPartnerStores,
    merchant,
    defaultLoadCustomerAddress
  } = useContext(LandingContext)

  const { customerDetails } = useShop()
  const { areGoogleApisLoaded } = useGoogleApis()

  const { handleLocationChange, handleLocationSelect, selectCustomerAddress } =
    useLandingLocation({
      setCurrentLocation,
      setUseCustomerAddress,
      currentLocation
    })

  const { getSessionStorageItem: getSavedCurrentLocation } =
    useSessionStorage<CurrentLocationState>('saved-current-location')

  const [searchValue, setSearchValue] = useState(currentLocation?.address || '')
  // sortedStores are the PartnerStoreBrowse responses from the BE with sort criteria
  const [sortedStores, setSortedStores] =
    useState<PartnerStoreBrowse[]>(sortedPartnerStores)
  // filteredStores are locally filtered for the child BrowseStoresList
  const [filteredStores, setFilteredStores] =
    useState<PartnerStoreBrowse[]>(sortedPartnerStores)
  const [isControlHeaderStuck, setIsControlHeaderStuck] =
    useState<boolean>(false)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const navbarHeight = document.getElementById('nav-bar-header')?.offsetHeight

  const handleStoreSearch = (input: string) => {
    // handleLocationChange will handle the currentLocation reset
    handleLocationChange(input, { clearLocationOnChange: true })
    setSearchValue(input)

    if (!sortedStores?.length) return
    if (isAddressEligible(input)) {
      setFilteredStores(filterStores(input, sortedStores))
    } else {
      setFilteredStores(sortedStores)
    }
  }

  // listen to any currentLocation changes so we can update the search input value
  useEffect(() => {
    if (currentLocation?.address) {
      setSearchValue(currentLocation.address)
    }
  }, [currentLocation?.address])

  // Autofill Address - only when address input is empty and user has not cleared it before
  useEffect(() => {
    autofillLocation({
      currentLocation,
      savedCurrentLocation: getSavedCurrentLocation(),
      customerDetails,
      defaultLoadCustomerAddress,
      handleLocationSelect,
      selectCustomerAddress,
      areGoogleApisLoaded
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDetails?.addresses, areGoogleApisLoaded])

  // listen to any changes in stores from a potential location change
  useEffect(() => {
    setSortedStores(sortedPartnerStores)
    setFilteredStores(sortedPartnerStores)
  }, [sortedPartnerStores])

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    setIsControlHeaderStuck(!entries[0].isIntersecting)
  }

  // Create intersectionObs to check if controlHeader has become sticky
  const controlHeaderObserver = useIntersectionObserver(
    handleIntersection,
    sentinelRef.current,
    {
      root: null,
      threshold: 0,
      rootMargin: `-${navbarHeight}px 0px 0px 0px`
    }
  )
  useEffect(() => {
    return () => {
      controlHeaderObserver?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!stores?.length && !searchValue) {
    return (
      <Container>
        <ClosedStoresMessage>
          {merchant && merchant.closed_store_message}
        </ClosedStoresMessage>
      </Container>
    )
  }

  return (
    <Container>
      <ControlsHeaderSentinel ref={sentinelRef} />
      <StickyHeaderContainer
        isControlHeaderStuck={isControlHeaderStuck}
        navbarHeight={navbarHeight}
      >
        <PseudoFormInput
          searchValue={searchValue}
          address={currentLocation?.address || ''}
          customerAddresses={customerDetails?.addresses}
          onChange={handleStoreSearch}
          onSelect={handleLocationSelect}
          id='store-search-input'
          selectCustomerAddress={selectCustomerAddress}
          inputPlaceholder='Type your Postcode or Store name'
          leftIcon={<IoIosSearch />}
          stores={currentLocation?.address ? sortedStores : filteredStores}
          closeModalOnSelect={false}
        />
        <StoreSortControlContainer isControlHeaderStuck={isControlHeaderStuck}>
          <StoreSortControl isControlHeaderStuck={isControlHeaderStuck} />
        </StoreSortControlContainer>
      </StickyHeaderContainer>
      <BrowseStoresList
        stores={currentLocation?.address ? sortedStores : filteredStores}
      />
    </Container>
  )
}

const Container = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: 0,
  minHeight: '100vh', // maintain scroll position when store list shortens
  [theme.mediaQueries.viewport7]: {
    padding: `0 ${theme.space?.[3]}px`,
    minHeight: 'unset'
  }
}))

const StoreSortControlContainer = styled.div<
  StyledHTMLElement & { isControlHeaderStuck: boolean }
>(({ theme, isControlHeaderStuck }) => ({
  padding: isControlHeaderStuck ? '8px 0' : '12px 0',
  transition: 'all 200ms ease-in',

  [theme.mediaQueries.viewport7]: {
    padding: isControlHeaderStuck ? '8px 12px' : '18px 12px'
  }
}))

const StickyHeaderContainer = styled(ControlsHeaderSticky)<
  StyledHTMLElement & { isControlHeaderStuck: boolean; navbarHeight?: number }
>(({ theme, isControlHeaderStuck, navbarHeight = 73 }) => ({
  top: navbarHeight + (isControlHeaderStuck ? 0 : 16),
  padding: isControlHeaderStuck ? '16px 16px 0' : '0 16px 16px',
  margin: '0 -16px', // extend to full width over the parent's padding
  zIndex: 2,

  [theme.mediaQueries.viewport7]: {
    padding: isControlHeaderStuck ? '16px 32px 0' : '0 32px 16px',
    margin: '0 -32px' // extend to full width over the parent's padding
  }
}))

export default LandingStoreList
